import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Package, DollarSign, Clock, CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    pending_review: { label: 'Pending Review', color: 'bg-yellow-500/20 text-yellow-400' },
    priced: { label: 'Priced', color: 'bg-blue-500/20 text-blue-400' },
    paid: { label: 'Paid', color: 'bg-emerald-500/20 text-emerald-400' },
    in_progress: { label: 'In Production', color: 'bg-purple-500/20 text-purple-400' },
    completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' },
    rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400' },
};

export default function AdminContentRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchRequests = async () => {
        try {
            const res = await fetch(`${API}/api/admin/content-requests`, { credentials: 'include' });
            if (res.ok) setRequests(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchRequests(); }, []);

    const handleUpdate = async (requestId) => {
        setSaving(true);
        try {
            const data = editData[requestId] || {};
            const res = await fetch(`${API}/api/admin/content-requests/${requestId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(data)
            });
            if (res.ok) {
                fetchRequests();
                setExpandedId(null);
                setEditData(prev => { const n = { ...prev }; delete n[requestId]; return n; });
            } else {
                const err = await res.json();
                alert(err.detail || 'Update failed');
            }
        } catch (e) { console.error(e); alert('Error updating request'); }
        finally { setSaving(false); }
    };

    const updateField = (id, field, value) => {
        setEditData(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };

    const stats = {
        pending: requests.filter(r => r.status === 'pending_review').length,
        paid: requests.filter(r => r.status === 'paid').length,
        in_progress: requests.filter(r => r.status === 'in_progress').length,
        completed: requests.filter(r => r.status === 'completed').length,
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-content-requests-page">
                <div>
                    <h1 className="text-2xl font-bold text-white" data-testid="admin-content-title">Content Requests</h1>
                    <p className="text-sm text-white/50 mt-1">Manage branded content requests from operators</p>
                </div>

                <div className="grid grid-cols-4 gap-4">
                    {[
                        { label: 'Pending Review', value: stats.pending, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                        { label: 'Paid', value: stats.paid, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                        { label: 'In Production', value: stats.in_progress, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                        { label: 'Completed', value: stats.completed, color: 'text-green-400', bg: 'bg-green-500/10' },
                    ].map(s => (
                        <div key={s.label} className={`${s.bg} border border-white/5 rounded-lg p-4`}>
                            <p className="text-xs text-white/40">{s.label}</p>
                            <p className={`text-2xl font-bold ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>{s.value}</p>
                        </div>
                    ))}
                </div>

                {loading ? (
                    <div className="text-center py-12 text-white/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-16 bg-[#12151C] rounded-lg border border-white/5">
                        <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/40">No content requests yet</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {requests.map(req => {
                            const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending_review;
                            const isExpanded = expandedId === req.id;
                            const edit = editData[req.id] || {};
                            return (
                                <div key={req.id} className="bg-[#12151C] border border-white/10 rounded-lg overflow-hidden" data-testid={`admin-req-${req.id}`}>
                                    <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                                        <div className="flex items-center gap-4 flex-1">
                                            <div>
                                                <h3 className="text-white font-semibold">{req.title}</h3>
                                                <p className="text-xs text-white/40">{req.operator_name} - {new Date(req.created_at).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {req.price != null && <span className="text-[#D4AF37] font-bold">${req.price.toFixed(2)}</span>}
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                                            {isExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="border-t border-white/5 p-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-white/40 text-xs mb-1">Description</p>
                                                    <p className="text-white/70">{req.description || 'No description'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-xs mb-1">Notes from Operator</p>
                                                    <p className="text-white/70">{req.notes || 'None'}</p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="text-white/60 text-xs">Price (USD)</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={edit.price ?? req.price ?? ''}
                                                        onChange={e => updateField(req.id, 'price', parseFloat(e.target.value) || 0)}
                                                        placeholder="Set price..."
                                                        className="bg-[#0A0D14] border-white/10 text-white"
                                                        data-testid={`price-input-${req.id}`}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-white/60 text-xs">Status</Label>
                                                    <select
                                                        value={edit.status ?? ''}
                                                        onChange={e => updateField(req.id, 'status', e.target.value)}
                                                        className="w-full h-10 rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 text-sm"
                                                        data-testid={`status-select-${req.id}`}
                                                    >
                                                        <option value="">Keep current ({cfg.label})</option>
                                                        {req.status === 'pending_review' && <><option value="priced">Set as Priced</option><option value="rejected">Reject</option></>}
                                                        {req.status === 'priced' && <><option value="pending_review">Back to Review</option><option value="rejected">Reject</option></>}
                                                        {req.status === 'paid' && <><option value="in_progress">Start Production</option><option value="rejected">Reject</option></>}
                                                        {req.status === 'in_progress' && <option value="completed">Mark Completed</option>}
                                                    </select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-white/60 text-xs">Admin Notes</Label>
                                                    <Input
                                                        value={edit.admin_notes ?? req.admin_notes ?? ''}
                                                        onChange={e => updateField(req.id, 'admin_notes', e.target.value)}
                                                        placeholder="Notes for operator..."
                                                        className="bg-[#0A0D14] border-white/10 text-white"
                                                        data-testid={`notes-input-${req.id}`}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex justify-end gap-2 pt-2">
                                                <Button variant="outline" className="border-white/20 text-white/60" onClick={() => { setExpandedId(null); setEditData(prev => { const n = { ...prev }; delete n[req.id]; return n; }); }}>Cancel</Button>
                                                <Button onClick={() => handleUpdate(req.id)} disabled={saving} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid={`save-btn-${req.id}`}>
                                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />} Save Changes
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
